/**
 * SSE notifications stream with token refresh and backoff retry.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { auth } from '@/config/firebase';
import { NOTIFICATION_QUERY_KEYS } from '@/constants/shared-catalog';
import { notificationsStreamUrl } from '@/services/notifications.service';

/** Initial SSE reconnect delay after a dropped stream. */
const RETRY_BASE_MS = 2_000;
/** Maximum SSE reconnect backoff. */
const RETRY_CAP_MS = 30_000;
/** Retry delay while Firebase has no current user. */
const NO_USER_RETRY_MS = 5_000;
/** Rebuild the SSE connection before the Firebase ID token expires. */
const TOKEN_REFRESH_MS = 50 * 60 * 1_000;

/**
 * Opens an SSE stream to `/api/v1/notifications/stream` and invalidates the notifications
 * queries whenever a `notification.*` event is received.
 *
 * Native `EventSource` cannot set headers, so the Firebase ID token is passed as a `?token=`
 * query parameter. The backend `FirebaseJwtGuard` accepts both. The connection is rebuilt
 * ~50 minutes after each successful open to pick up a fresh token (Firebase tokens expire
 * at 60 min).
 */
export function useNotificationsStream(enabled = true): void {
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!enabled) return;

    let es: EventSource | null = null;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let retryDelay = RETRY_BASE_MS;

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const invalidate = () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.all });
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.unreadCount });
    };

    const connect = async () => {
      if (disposed) return;
      clearTimer();

      const user = auth.currentUser;
      if (!user) {
        timer = setTimeout(connect, NO_USER_RETRY_MS);
        return;
      }

      let token: string;
      try {
        token = await user.getIdToken();
      } catch (err) {
        console.warn('[notifications-stream] getIdToken failed', err);
        timer = setTimeout(connect, NO_USER_RETRY_MS);
        return;
      }

      const streamUrl = notificationsStreamUrl(token);
      if (!streamUrl) {
        console.warn('[notifications-stream] VITE_API_URL is empty; cannot open SSE');
        return;
      }

      es?.close();
      es = new EventSource(streamUrl, {
        withCredentials: false,
      });

      es.addEventListener('notification.created', invalidate);
      es.addEventListener('notification.read', invalidate);
      es.addEventListener('notification.read_all', invalidate);

      es.onopen = () => {
        retryDelay = RETRY_BASE_MS;
        clearTimer();
        timer = setTimeout(connect, TOKEN_REFRESH_MS);
      };

      es.onerror = () => {
        es?.close();
        es = null;
        if (disposed) return;
        clearTimer();
        timer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, RETRY_CAP_MS);
      };
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') invalidate();
    };
    document.addEventListener('visibilitychange', onVisibility);

    void connect();

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimer();
      es?.close();
      es = null;
    };
    // queryClient is stable; avoid re-running the effect and opening duplicate EventSources
  }, [enabled]);
}
